import { BookOpen, Home, ShieldCheck } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { ViewModeSwitcher } from '@/components/ViewModeSwitcher'
import { OverallStatusLink } from '@/components/board/OverallStatusLink'
import { AccountMenu } from './AccountMenu'
import { EnvironmentBadge } from './EnvironmentBadge'
import { NotificationBell } from './NotificationBell'
import { PipelineReportLink } from './PipelineReportLink'
import { TenancySwitcher } from './TenancySwitcher'
import { VersionChip } from './VersionChip'
import { AuditCenterLink } from './AuditCenterLink'
import { ShiftDashboardLink } from './ShiftDashboardLink'
import { MyTasksLink } from './MyTasksLink'
import { SupportHeaderLinks } from '@/components/support/SupportHeaderLinks'
import { MainMenu } from './MainMenu'
import { usePendingReviewCount } from '@/hooks/usePendingReviews'
import { sectionTitle, topBarTitle } from '@/lib/topBarTitle'
import { useCurrentPageTitle } from './PageTitle'

export function TopBar() {
  const { pathname } = useLocation()
  // "Header - Title" (z8nrz7e46x): the section from the route, then what the
  // page calls itself when that differs.
  const title = topBarTitle(sectionTitle(pathname), useCurrentPageTitle())

  return (
    <header className='flex h-14 shrink-0 items-center gap-3 border-b bg-background px-6'>
      {/* Every page and tab A–Z (z8nrz7f7g1, z8nrz7f7g3). First, where an app
          menu conventionally sits. */}
      <MainMenu />

      {/* The brand mark, linking to Home as on most apps. Named for the brand:
          the Home icon by the title is the one called "Home". */}
      <Link
        to='/dashboard'
        title='Home'
        aria-label='Title21'
        className='flex h-8 shrink-0 items-center rounded-md px-1 transition-opacity hover:opacity-80'
      >
        <img src='/images/title21-mark.png' alt='' width={466} height={293} className='h-6 w-auto' />
      </Link>

      {/* Sits where the logo would be on /dashboard. Renders nothing on
          production and on every client deployment. */}
      <EnvironmentBadge />

      <div className='h-4 w-px bg-border' />
      {/* Home, then where the user is. A page without a title shows the icon alone. */}
      <div className='flex min-w-0 flex-1 items-center gap-1.5'>
        <Link
          to='/dashboard'
          title='Home'
          aria-label='Home'
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <Home className='h-4 w-4' />
        </Link>
        {title && <h2 className='truncate text-sm font-semibold text-foreground'>{title}</h2>}
      </div>

      {/* Every audit trail, from anywhere (z8nrz7dq6q). */}
      <AuditCenterLink />

      {/* The Shift Dashboard, beside Audit (z8nrz7fbcj). */}
      <ShiftDashboardLink />

      {/* My Tasks, beside Audit (z8nrz7fh6h). */}
      <MyTasksLink />

      {/* Title21 Support, and Report an issue from any page. */}
      <SupportHeaderLinks />

      {/* Every open procedure, all types. */}
      <OverallStatusLink placement='topbar' />

      <VersionChip />

      <TenancySwitcher />

      <ReviewWorklistLink />

      <Link
        to='/wiki'
        title='Wiki'
        className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
      >
        <BookOpen className='h-4 w-4' />
      </Link>

      {/* Dev only. See PipelineReportLink. */}
      <PipelineReportLink className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground' />

      {/* Desktop / Mobile / Auto. Session-scoped; see src/lib/viewMode.ts. */}
      <ViewModeSwitcher />

      <NotificationBell />

      <AccountMenu />
    </header>
  )
}

/**
 * The way into the review worklist from any screen (z8nrz7dt41 AC1), with how
 * many forms are waiting. Only for someone who may review at all.
 */
function ReviewWorklistLink() {
  const { data, canReview } = usePendingReviewCount()
  if (!canReview) return null
  // Only reviews that can be signed now; ones waiting on their form are listed
  // on the page but are not yet something to do.
  const count = data?.ready ?? 0
  return (
    <Link
      to='/my-steps?tab=reviews'
      title={count > 0 ? `${count} review${count === 1 ? '' : 's'} ready for you to sign` : 'My reviews'}
      aria-label='My reviews'
      className='relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
    >
      <ShieldCheck className='h-4 w-4' />
      {count > 0 && (
        <span className='absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white'>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  )
}
